import React from 'react';
import { availableBriefModelOptions } from './briefModelOptions';
import { useConfig } from '../../contexts/FrontendConfigContext';

function BriefGenerationSettingsModal({
  isOpen,
  title,
  canEdit,
  canSelectGenerationMethod,
  languages,
  values,
  onChange,
  onCancel,
  onContinue,
  showCreateNewCopyOption = false,
  isSubmitting = false,
  continueLabel = 'Generate draft',
  modalTestId,
  isAdmin = false,
  buckets = [],
  // The buckets this (non-admin) user owns that have reformatting configured.
  // They choose per draft which one to apply; default is none.
  ownedBuckets = [],
  // Publisher Bucket Intelligence (spec §7): when the selected target bucket
  // is in Include mode AND the story has competitor sources, force the
  // generation method to Claim-Free and disable the dropdown. `null` when no
  // bucket is selected or the check hasn't finished.
  competitorContext = null,
}) {
  const canPickGenerationMethod = canSelectGenerationMethod ?? canEdit;
  // Hook must run before the early return below.
  const { CLAUDE_MODELS_AVAILABLE } = useConfig();
  const modelOptions = availableBriefModelOptions(!!CLAUDE_MODELS_AVAILABLE);
  if (!isOpen) return null;

  const languageOptions = Array.isArray(languages) ? languages.filter(Boolean) : [];

  return (
    <div className="modal" data-testid={modalTestId}>
      <div className="modal-content brief-modal-content" style={{ borderRadius: '12px', padding: '0', maxWidth: '500px', margin: '0 auto', overflow: 'hidden' }}>
        <div className="brief-modal-header">
          {/* Story headlines are long; .brief-modal-title clamps to one line so
              the form below keeps its vertical room. */}
          <h2 className="brief-modal-title" title={title || 'Draft'}>
            {title || 'Draft'}
          </h2>
        </div>
        <div className="brief-modal-scroll">
          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <h4>Citation Style</h4>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => onChange('promptType', 'DEFAULT_BRIEF')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: values.promptType === 'DEFAULT_BRIEF' ? '#FF6B35' : '#F7F9FC',
                    color: values.promptType === 'DEFAULT_BRIEF' ? 'white' : '#3C4A5E',
                    border: values.promptType === 'DEFAULT_BRIEF' ? 'none' : '1px solid rgba(60,74,94,0.20)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: values.promptType === 'DEFAULT_BRIEF' ? '600' : '400',
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => onChange('promptType', 'DEFAULT_BRIEF_2_CITATIONS')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: values.promptType === 'DEFAULT_BRIEF_2_CITATIONS' ? '#FF6B35' : '#F7F9FC',
                    color: values.promptType === 'DEFAULT_BRIEF_2_CITATIONS' ? 'white' : '#3C4A5E',
                    border: values.promptType === 'DEFAULT_BRIEF_2_CITATIONS' ? 'none' : '1px solid rgba(60,74,94,0.20)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: values.promptType === 'DEFAULT_BRIEF_2_CITATIONS' ? '600' : '400',
                  }}
                >
                  Two
                </button>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <h4>Language</h4>
              <select
                id="language-select"
                aria-label="Language"
                value={values.language}
                onChange={(event) => onChange('language', event.target.value)}
                style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
              >
                {languageOptions.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <h4>Generation Model</h4>
              <select
                id="model-select"
                aria-label="Generation Model"
                value={values.model}
                onChange={(event) => onChange('model', event.target.value)}
                style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
              >
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: 1 }}>
              <h4>Translation Model</h4>
              <select
                id="model-translate-select"
                aria-label="Translation Model"
                value={values.translationModel}
                onChange={(event) => onChange('translationModel', event.target.value)}
                style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
              >
                <option value="gpt">GPT-5</option>
                <option value="deepl">DEEP-L</option>
                <option value="claude">Claude</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <h4>Reformat Model</h4>
              <select
                id="model-reformat-select"
                aria-label="Reformat Model"
                value={values.reformatModel || ''}
                onChange={(event) => onChange('reformatModel', event.target.value)}
                style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
              >
                <option value="">Same as Generation Model</option>
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#3C4A5E', marginTop: '5px' }}>
                Model used for the journalistic reformat pass on the draft. Leave as "Same as Generation Model" to keep prior behavior.
              </p>
            </div>
          </div>

          {isAdmin ? (
            <div style={{ marginBottom: '20px' }}>
              <h4>Target Client / Bucket <span style={{ color: '#3C4A5E', fontSize: '12px', fontWeight: 400 }}>(admin)</span></h4>
              <select
                aria-label="Target Bucket"
                value={values.targetBucketId || ''}
                onChange={(event) => onChange('targetBucketId', event.target.value)}
                style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
              >
                <option value="">— None (no per-bucket reformatting) —</option>
                {(buckets || []).map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}{b.targetLanguage ? ` — ${b.targetLanguage}` : ''}{b.aiReformattingEnabled ? ' ✨' : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#3C4A5E', marginTop: '5px' }}>
                When set, the generated brief is rewritten through the bucket's AI Reformatting prompts (admin only).
                Choosing a bucket also switches Language to that bucket's language — change it above if you want something else.
              </p>
            </div>
          ) : null}

          {!isAdmin && ownedBuckets.length > 0 ? (
            <div style={{ marginBottom: '20px' }}>
              <h4>Draft Formatting</h4>
              <select
                aria-label="Draft Formatting"
                value={values.targetBucketId || ''}
                onChange={(event) => onChange('targetBucketId', event.target.value)}
                style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
              >
                <option value="">— None (no reformatting) —</option>
                {ownedBuckets.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}{b.targetLanguage ? ` — ${b.targetLanguage}` : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#3C4A5E', marginTop: '5px' }}>
                Choose a bucket to rewrite this draft through its AI Reformatting prompts. Your last choice is remembered.
                Choosing one also switches Language to that bucket's language — change it above if you want something else.
              </p>
            </div>
          ) : null}

          {canPickGenerationMethod ? (
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div style={{ flex: 1 }}>
                <h4>Generation Method <span style={{ color: '#f4b400', fontSize: '12px' }}>(Beta)</span></h4>
                {competitorContext?.forceClaimFree ? (
                  <>
                    <select
                      id="generation-method-select"
                      aria-label="Generation Method"
                      value="claimFree"
                      disabled
                      style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F', opacity: 0.85 }}
                    >
                      <option value="claimFree">Claim-free (Direct from Articles)</option>
                    </select>
                    <div
                      role="note"
                      aria-live="polite"
                      style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '6px', background: 'rgba(255,152,0,0.10)', border: '1px solid rgba(255,152,0,0.35)', color: '#ad6300', fontSize: '12px' }}
                    >
                      {competitorContext?.restrictedProduction
                        ? `This bucket draws drafts only from sources matching the suggestion's filters${
                            competitorContext?.restrictedScopeLabel ? ` (${competitorContext.restrictedScopeLabel})` : ''
                          }. Shared claims can carry content from other articles, so only Claim-Free generation is available.`
                        : 'This story includes articles from direct competitors of the target publisher. Only Claim-Free generation is permitted so competitor content stays out of the draft.'}
                    </div>
                  </>
                ) : (
                  <>
                    <select
                      id="generation-method-select"
                      aria-label="Generation Method"
                      value={values.generationMethod}
                      onChange={(event) => onChange('generationMethod', event.target.value)}
                      style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid rgba(60,74,94,0.20)', backgroundColor: '#F7F9FC', fontSize: '14px', color: '#0B1E3F' }}
                    >
                      <option value="claims">Claims-based</option>
                      <option value="claimFree">Claim-free (Direct from Articles)</option>
                      {canEdit && <option value="both">Compare Both</option>}
                    </select>
                    <p style={{ fontSize: '12px', color: '#3C4A5E', marginTop: '5px' }}>
                      {values.generationMethod === 'claims' && 'Uses extracted claims to synthesize the draft.'}
                      {values.generationMethod === 'claimFree' && 'Generates draft directly from up to 3 source articles.'}
                      {values.generationMethod === 'both' && 'Runs both methods in parallel for comparison.'}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {showCreateNewCopyOption ? (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', color: '#0B1E3F', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  aria-label="Overwrite current draft"
                  checked={Boolean(values.overwriteExisting)}
                  onChange={(event) => onChange('overwriteExisting', event.target.checked)}
                  style={{ marginTop: '2px' }}
                />
                <span>Overwrite the current draft instead of creating a new copy</span>
              </label>
            </div>
          ) : null}
        </div>

        {/* Pinned: the primary action stays reachable however tall the form gets. */}
        <div className="brief-modal-footer">
          <button
            type="button"
            onClick={onCancel}
            style={{ padding: '10px 24px', backgroundColor: '#fff', color: '#3C4A5E', border: '1px solid rgba(60,74,94,0.20)', borderRadius: '8px', cursor: 'pointer', width: 'auto', fontSize: '14px', fontWeight: '500' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="next-button"
            onClick={onContinue}
            disabled={isSubmitting}
            style={{ padding: '10px 24px', backgroundColor: '#FF6B35', color: 'white', border: 'none', borderRadius: '8px', cursor: isSubmitting ? 'not-allowed' : 'pointer', width: 'auto', fontSize: '14px', fontWeight: '500', opacity: isSubmitting ? 0.7 : 1 }}
          >
            {isSubmitting ? 'Generating...' : continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BriefGenerationSettingsModal;
